import ViewViewModel from '@/ViewModels/ViewViewModel';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';
import StorageHelper from '@/Utility/StorageHelper';
import UserSpecificDataHelper from '@/Utility/UserSpecificDataHelper';
import LocatorHelper from '@/Utility/LocatorHelper';
import ListViewHelper from '@/Utility/ListViewHelper';
import FilterLinkViewModel from '@/Components/Links/FilterLink/FilterLinkViewModel';
import NavigationService from '@/Management/NavigationService';
import PublicFilterEditLinkViewModel from '@/Components/Links/FilterLink/PublicFilterEditLinkViewModel';
import {SearchListViewViewModelConstructorParameter} from '#/CoreFrameInterfaces';
import {Handler, IDataGridViewModel, IDataGridViewModelConstructorParameter} from '#/DefaultViewExtensionInterfaces';
import {IHideConnectedElementsRequestDTO, IListViewDTO, IColumnDTO} from "coreframetypings/dto";
import UIListener from '@/UIListener/UIListener';
import DataGridViewModelT from '@/ViewModels/Controls/DataGridViewModel';
import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import ToolBarViewModelT from '@/ViewModels/Controls/ToolBarViewModel';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import FilterViewModelT from '@/ViewModels/Controls/FilterViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class SearchListViewViewModel extends ViewViewModel {
    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private ToolBarViewModel = ComponentManager.getViewModel<typeof ToolBarViewModelT>('ToolBarViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private DataGridViewModel = ComponentManager.getViewModel<typeof DataGridViewModelT>('DataGridViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private FilterViewModel = ComponentManager.getViewModel<typeof FilterViewModelT>('FilterViewModel');

    /*
     * Handler für das Schließen des Fensters
     */
    public onClose: Handler;

    /*
     * Das DTO der ListView
     */
    public listView: IListViewDTO | null;

    /*
     * Gibt an, ob mehrere Element ausgewählt werden können
     */
    public allowMultiSelect: boolean;

    /*
     * Ein Callback
     */
    public callback: ((toPdoIds: Array<string>) => void) | null;

    /*
     * Die PDOId des Contexts
     */
    public contextPdoId: string;

    /*
     * Der Typ des Contexts
     */
    private readonly contextTypeName: string;

    /*
    * Sollen die verbundenen Elemente angezeigt werden
    */
    private readonly showConnectedObjects: boolean;

    /*
     * Gibt an, ob aktuelle Elemente angezeigt werden
     */
    public showCurrentObjects: KnockoutObservable<boolean>;

    /*
     * optionaler Historisierungszeitpunkt
     */
    public historizationDate: string | null;

    /*
     * Der aktuelle UIListener
     */
    public uiListener: UIListener | null;

    /*
     * Der serialisierte Filter für das Grid
     */
    public serializedFilter: KnockoutObservable<string>;

    /**
     * Columns that must be removed from column definition for searchlist
     */
    public columnsToRemove: string[];

    /*
     * Wenn gesetzt, werden die bereits verbundenen Elemente herausgefiltert
     */
    public hideConnectedElementsRequest: IHideConnectedElementsRequestDTO | null;

    /**
     * Custom searchlist title, which will be shown instead of the plural ergname.
     */
    public customTitle: string;

    /*
     * Das ViewModel des DataGrids
     */
    public dataGrid: DataGridViewModelT;

    /*
     * Das Window-Control
     */
    public window: WindowViewModelT;

    /*
     * Das ViewModel der ToolBar
     */
    public toolBar: ToolBarViewModelT;

    /*
     * Das ViewModel des Okay-Buttons
     */
    public okay: ButtonViewModelT;

    /*
     * Das ViewModel des Cancel-Buttons
     */
    public cancel: ButtonViewModelT;

    constructor(params: SearchListViewViewModelConstructorParameter) {
        super(params);

        this.onClose = this.handlerOrDefault(params.onClose);
        this.listView = params.listView || null;
        this.allowMultiSelect = params.allowMultiSelect || false;
        this.callback = params.callback || null;
        this.contextPdoId = this.paramOrDefault(params.contextPdoId, "-1");
        this.contextTypeName = this.paramOrDefault(params.contextTypeName, null);
        this.showConnectedObjects = this.paramOrDefault(params.showConnectedObjects, false);
        this.showCurrentObjects = this.observableOrDefault(params.showCurrentObjects, true);
        this.historizationDate = this.paramOrDefault(params.historizationDate, null);
        this.uiListener = null;
        this.serializedFilter = this.observableOrDefault(params.serializedFilter);
        this.columnsToRemove = this.paramOrDefault(params.columnsToRemove, []);
        this.hideConnectedElementsRequest = this.paramOrDefault(params.hideConnectedElementsRequest, () => this.createHideConnectedElementsRequest(this.listView));
        this.customTitle = this.paramOrDefault(params.customTitle, '');

        this.dataGrid = (() => this.createDataGrid())();
        (() => this.createFilterSubscriptions())();
        this.window = (() => this.createWindow())();
        this.toolBar = (() => this.createToolBar())();
        this.okay = (() => this.createOkayButton())();
        this.cancel = (() => this.createCancelButton())();
    }

    private createHideConnectedElementsRequest(listView: IListViewDTO): IHideConnectedElementsRequestDTO | null {
        if (listView == null)
            return null;

        return {
            hide: listView.hideConnectedElements,
            associationName: listView.relationName
        };
    }

    /*
     * Aktualisiert das DataGrid
     */
    public refresh() {
        this.dataGrid.refresh();
    }

    private createDataGrid(): DataGridViewModelT {
        const columnDefinitionObs = ListViewHelper.loadColumnDefinitions(this.listView.name + '_ColumnDefinition', this.listView.columns, [], false);
        const columnDefinition = columnDefinitionObs().filter((col) => {
            return this.columnsToRemove.indexOf(col.name) === -1;
        });

        const columnPreset = StorageHelper.bind(this.listView.name + '_ColumnPreset', {});
        const pageSize = UserSpecificDataHelper.loadUserSpecificData(this.listView.name + '_PageSize', RADApplication.getInstance().configuration.possibleAssoziationListViewPages[0]);
        let orderBy;
        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.SaveListViewSorting')) {
            orderBy = UserSpecificDataHelper.loadUserSpecificData(this.listView.name + '_Order', null);
        } else {
            const observable = ko.observable(null);
            orderBy = ko.computed({
                read: () => {
                    return observable();
                },
                write: (newVal) => {
                    observable(newVal);
                }
            });
        }

        this.addComputed(columnDefinitionObs);
        this.addComputed(columnPreset);
        this.addComputed(pageSize);
        this.addComputed(orderBy);

        const parameters = this.createDataGridViewModelParameters(columnDefinition, columnPreset, pageSize, orderBy);
        return new this.DataGridViewModel(parameters);
    }

    private createDataGridViewModelParameters(
        columnDefinition: IColumnDTO[],
        columnPreset: KnockoutComputed<{}>,
        pageSize: KnockoutComputed<number>,
        orderBy: KnockoutComputed<any>) 
        : IDataGridViewModelConstructorParameter {
        const parameters : IDataGridViewModelConstructorParameter = {
            parentLocator: this.locator,
            locator: 'datagrid',
            filter: new this.FilterViewModel({
                parentLocator: this.locator,
                locator: 'datagrid.filter',
                onSubmit: () => {
                    this.refresh();
                },
                serializedFilter: this.serializedFilter,
                includeAllPrincipals: this.listView.includeAllPrincipals,
                includeBasePrincipals: this.listView.includeBasePrincipals,
                includeSubPrincipals: this.listView.includeSubPrincipals,
                listViewPdoId: this.listView.listViewID,
                pdTypeName: this.listView.typeName,
                shouldShowChips: this.listView.showFilter,
                shouldShowSaveInputs: true,
                shouldShowSorting: true
            }),
            identity: 'PDO_ID',
            contextPdoId: this.contextPdoId,
            pdTypeName: this.listView.typeName,
            columnDefinition: columnDefinition as any, // TODO: Types
            columnPreset: columnPreset,
            pageSizes: RADApplication.getInstance().configuration.possibleAssoziationListViewPages,
            pageSize: pageSize,
            page: 1,
            orderBy: orderBy,
            onDoubleClickHandler: (pdoId, dataRow) => {
                const pdoIDs = [pdoId];
                this.callback(pdoIDs);
                this.window.isOpen(false);
            },
            isMultiColumnSelect: this.allowMultiSelect,
            isMultiColumnSort: true, // this.listView.multiColumnSort, --> IMMER false
            staticFilter: this.listView.staticFilterExpression,
            isPrincipalIndependent: this.listView.isPrincipalIndependent,
            includeAllPrincipals: this.listView.includeAllPrincipals,
            includeBasePrincipals: this.listView.includeBasePrincipals,
            includeSubPrincipals: this.listView.includeSubPrincipals,
            historyDate: this.historizationDate,
            showCurrentObjects: this.showCurrentObjects,
            hideConnectedElementsRequest: this.hideConnectedElementsRequest,
            rowLimit: this.listView.rowLimit,
        };

        // Wir wollen die bereits verbundenen Objekte anzeigen
        if (this.showConnectedObjects) {
            parameters.associationName = this.listView.relationName;
            parameters.associationPdoId = this.contextPdoId;
            parameters.associationType = this.contextTypeName;
            parameters.defaultOrder = this.listView.defaultOrder;
        }
        else {
            parameters.defaultOrder = this.mapToLVDefaultOrder(this.listView.defaultOrder);
        }
        
        return parameters;
    }

    /**
     * Die Default Order einer ALV setzt sich aus der Default Order der LV und dem Assoziationsnamen zusammen.
     * Da in dieser View nicht über die Assoziation gegangen wird, wird der Assoziationsname aus der Default Order entfernt.
     */
    private mapToLVDefaultOrder(alvDefaultOrder: string | null): string | null {
        return alvDefaultOrder?.split(',').map(p => this.removeAssociationName(p)).join(',') ?? null;
    }

    private removeAssociationName(orderPart: string): string {
        let propertyParts = orderPart.split('.');
        if (propertyParts.length === 2) {
            return orderPart;
        }
        const toRemove = propertyParts[1];
        propertyParts = _.without(propertyParts, toRemove);
        return propertyParts.join('.');
    }

    private createFilterSubscriptions() {
        if (this.dataGrid.filter != null) {
            // refresh the grid when the sort-state of any sortElement-VM changes
            this.defineSubscription(this.dataGrid.filter.orderBy,
                filterVM_orderBy => {
                    if (this.dataGrid.filter.suspendOrderByUpdateToGrid !== true) {
                        this.dataGrid.onSortChanged(filterVM_orderBy);
                    }
                });

            // forward changes from grid-ordering to filter-VM-ordering
            this.defineSubscription((this.dataGrid as IDataGridViewModel).orderBy, orderBy => {
                this.dataGrid.filter.suspendOrderByUpdateToGrid = true;
                this.dataGrid.filter.orderBy(orderBy);
                this.dataGrid.filter.suspendOrderByUpdateToGrid = false;
            });

            this.defineSubscription(this.dataGrid.filter.serializedFilter, (sf) => {
                NavigationService.getInstance().setHiddenParameterToAddress(this.contextPdoId, 'SerializedFilter', sf);
            });
        }
    }

    private createWindow(): WindowViewModelT {
        return new this.WindowViewModel({
            parentLocator: this.locator,
            locator: 'window',
            isOpen: true,
            width: '60%',
            isModal: true,
            allowClose: false,
            centerOnWindowResize: true,
            isSearchListViewWindow: true,
            onClose: this.onClose,
            headline: new this.LabelViewModel({
                ergName: this.getHeadLine(),
                postfix: new this.LabelViewModel({
                    caption: this.defineComputed(() => {
                        if (this.dataGrid.extentVisible) {
                            return '';
                        }

                        const gridData = this.dataGrid.data();

                        // TODO: Types
                        if ((gridData as any).extent) {
                            return ' (' + (gridData as any).extent + ')';
                        }

                        return ' (0)';
                    })
                })
            })
        });
    }

    private getHeadLine(): string {
        if (this.customTitle)
            return this.customTitle;

        return this.listView.ergNamePluralized;
    }

    private createToolBar(): ToolBarViewModelT {
        const toolbarLabel = (params) => {
            params.isVisible = Config.listView.ShowToolbarText;
            return new this.LabelViewModel(params);
        };

        const filterLinkLabel = toolbarLabel({
            parentLocator: this.locator,
            locator: 'toolbar.filter.label',
            ergName: 'FILTER'
        });

        return new this.ToolBarViewModel({
            parentLocator: this.locator,
            locator: 'toolbar',
            primary: (() => {
                const linkViewModels = [];

                if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowButtonsInToolbarSearchlistviewwindow')) {
                    linkViewModels.push(new this.LinkViewModel({
                        parentLocator: this.locator,
                        locator: 'toolbar.apply',
                        isPrimaryLink: true,
                        label: toolbarLabel({
                            parentLocator: this.locator,
                            locator: 'toolbar.apply.label',
                            ergName: 'APPLY'
                        }),
                        image: new this.ImageViewModel({
                            parentLocator: this.locator,
                            locator: 'toolbar.apply.image',
                            source: Config.inlineImages.LV_APPLY,
                            alternativeText: new this.LabelViewModel({
                                ergName: 'APPLY'
                            })
                        }),
                        tooltip: new this.LabelViewModel({
                            ergName: 'APPLY'
                        }),
                        isActive: false,
                        isVisible: true,
                        isEnabled: this.definePureComputed(() => {
                            const selected = this.dataGrid.selected();

                            if (this.allowMultiSelect && selected.length >= 1) {
                                return true;
                            } else if (!this.allowMultiSelect && selected.length === 1) {
                                return true;
                            }

                            return false;
                        }),
                        onClick: () => {
                            this.okay.onClick();
                        }
                    }));

                    linkViewModels.push(new this.LinkViewModel({
                        parentLocator: this.locator,
                        locator: 'toolbar.cancel',
                        label: toolbarLabel({
                            parentLocator: this.locator,
                            locator: 'toolbar.cancel.label',
                            ergName: 'CANCEL'
                        }),
                        image: new this.ImageViewModel({
                            parentLocator: this.locator,
                            locator: 'toolbar.cancel.image',
                            source: Config.inlineImages.LV_ABORT,
                            alternativeText: new this.LabelViewModel({
                                ergName: 'CANCEL'
                            })
                        }),
                        tooltip: new this.LabelViewModel({
                            ergName: 'CANCEL'
                        }),
                        isActive: false,
                        isVisible: true,
                        isEnabled: true,
                        onClick: () => {
                            this.cancel.onClick();
                        }
                    }));

                    linkViewModels.push({});
                }

                return linkViewModels;
            })(),
            secondary: (() => {
                const linkViewModels = [];

                linkViewModels.push(new this.LinkViewModel({
                    parentLocator: this.locator,
                    locator: 'toolbar.refresh',
                    label: toolbarLabel({
                        ergName: 'REFRESH_LIST',
                    }),
                    image: new this.ImageViewModel({
                        parentLocator: this.locator,
                        locator: 'toolbar.refresh.image',
                        source: Config.inlineImages.REFRESH,
                        alternativeText: new this.LabelViewModel({
                            ergName: 'REFRESH_TOOLTIP'
                        })
                    }),
                    tooltip: new this.LabelViewModel({
                        ergName: 'REFRESH_TOOLTIP'
                    }),
                    isActive: false,
                    isVisible: true,
                    isEnabled: true,
                    onClick: () => {
                        this.dataGrid.refresh();
                    }
                }));

                if (this.listView.showFilter) {
                    linkViewModels.push({});
                }

                const link = new FilterLinkViewModel({
                    dataSource: this.dataGrid,
                    listView: this.listView,
                    parentLocator: LocatorHelper.buildLocator(this.locator, 'toolbar'),
                    isActive: this.dataGrid?.filter?.isVisible() && !this.dataGrid?.filter?.editPublicChips(),
                    isEnabled: !this.dataGrid?.filter?.editPublicChips()
                });
                linkViewModels.push(link);

                linkViewModels.push(new PublicFilterEditLinkViewModel({
                    label: new this.LabelViewModel({
                        ergName: 'PUBLIC_FILTER_TOOLTIP',
                        isVisible: GlobalConfiguration.listView.ShowToolbarText
                    }),
                    filterSource: this.dataGrid.filter,
                    listView: this.listView,
                    parentLocator: LocatorHelper.buildLocator(this.locator, 'toolbar'),
                    filterLink: link,
                    isActive: this.dataGrid?.filter?.editPublicChips()
                }));

                return linkViewModels;
            })()
        });
    }

    private createOkayButton(): ButtonViewModelT {
        return new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'button.okay',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'button.okay.image',
                source: Config.inlineImages.WINDOW_OK,
                alternativeText: new this.LabelViewModel({
                    ergName: 'OKAY'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'button.okay.label',
                ergName: 'OKAY'
            }),
            onClick: () => {
                const selected = this.dataGrid.selected();
                this.callback(selected);
                this.window.isOpen(false);
            }
        });
    }

    private createCancelButton(): ButtonViewModelT {
        return new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'button.cancel',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'button.cancel.image',
                source: Config.inlineImages.WINDOW_CANCEL,
                alternativeText: new this.LabelViewModel({
                    ergName: 'CANCEL'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'button.cancel.label',
                ergName: 'CANCEL'
            }),
            onClick: () => {
                this.window.isOpen(false);
            }
        });
    }
}

ComponentManager.setViewModel('SearchListViewViewModel', SearchListViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');